import { IParams, Nullable } from '@app/types';
import { Checkbox } from '@hooks/envelopes/types';

import {
  TasksStatus,
  EnvelopeAllovedActions,
  EnvelopeDirection,
  EnvelopeFlowState,
  RecipientAccessAuthorizedBy,
  FlowStageState,
  DraftEnvelopePrepositionedMarkFont, DraftEnvelopePrepositionedMarkType, DraftEnvelopePrepositionedMarkFontType, EnvelopeFlowResult
} from '@const/consts'
import { IFileProperties } from '@app/utils/types';

export interface IEnvelope {
  oguid: string
  sender: IEnvelopeSender
  directions: Nullable<EnvelopeDirection[]>
  lastActionDateTime: string
  flowStartDateTime: string
  flowCompletedDateTime: string
  subject: string
  taskTotalCount: number
  taskCompletedCount: number
  allowedActions: EnvelopeAllovedActions[]
  fields: IEnvelopeFields
  flowState: EnvelopeFlowState
  recipients: IRecipients[]
  sharedLinkOguid: string
  /** Ящик, к которому относится конверт */
  box: IEnvelopeBox;
  /** Признак прочтения / не прочтения
    - для входящих конвертов соответствует состоянию назначенной на пользователя задачи (если задача прочитана, будет true)
    - для исходящих задач всегда возвращается true т.к. отправленный конверт считается автоматически прочтенным
  */
  isRead: boolean;
  /** Ярлыки */
  labels: IEnvelopeLabels;
  /** Контекст для поиска по query.like */
  content: string;
}

export interface IRecipients {
  email: string
  guestEmail: Nullable<string>
  fullName: string
}

/** Ящик конверта */
export interface IEnvelopeBox {
  oguid: string;
  email: string;
}

/** Ярлыки конверта */
export interface IEnvelopeLabels {
  /** Oid ярлыка */
  movedToLabel: Nullable<number>
  /** Разрешенные ярлыки */
  assignedLabels?: Nullable<number[]>
}

export interface IEnvelopePost {
  senderBoxOguid: string
  fields: IEnvelopeFields
  subject: string
  comment: string
  externalId: string
  files: string[]
  expirationDateTime: string
  flow: IEnvelopePostFlow[]
}

export interface ICurrentEnvelope extends IEnvelope {
  comment: Nullable<string>
  externalId: Nullable<string>
  unreadTodosCount: number
  files: Nullable<IEnvelopeFiles[]>
  currentUserTaskOguid: Nullable<string>
  expirationDateTime: string
  flow: ICurrentEnvelopeFlow[]
  sharedLinkOguid: string
}

export interface IEnvelopeGenerateLink {
  fields: IEnvelopeFields
  subject: string
  files: IEnvelopeFiles[]
  recipient: IEnveloperPostRecipient
  redirectTo: Nullable<string>
}

export interface IEnvelopeStopAgreementProcess {
  dateTime: string
  correlationId: string
  error: string
  message: string
  debugMessage: string
}

export interface IEnvelopeDiscussionObjectApproval {
  oguid: string
  readDateTime: string
  author: IEnvelopeUser
  recipient: IEnvelopeUser
  comment: string
  description: string
  todos: Array<object> // TODO: [ { } ], Array of objects or null (todo) Child TODO
  startedDateTime: string
  completedDateTime: string
}

export interface IEnvelopeSender {
  boxOguid: string
  boxEmail: string
  user: IEnvelopeUser
}

export interface IEnvelopeUser {
  oguid: string
  position: string
  email: string
  fullName: string
}

export interface ICurrentEnvelopeFlow {
  oguid: Nullable<string>
  state: Nullable<FlowStageState>
  tasks: IEnvelopeTasks[]
}

export interface IEnvelopeTasks {
  oguid: string
  type: TasksStatus
  readDateTime: Nullable<string>
  state: Nullable<FlowStageState>
  result: Nullable<EnvelopeFlowResult>
  recipient: ICurrentEnvelopeRecipient
  startedDateTime: string
  completedDateTime: Nullable<string>
  completedSigner: Nullable<IEnveloperCompletedSigner>
  completedEvidences: Nullable<IEnveloperCompletedEvidences>
}

export interface ICurrentEnvelopeRecipient {
  boxOguid: string
  email: string
  isHostMode: boolean
  guestEmail: Nullable<string>
  declaredSigner: Nullable<IEnveloperSigner>
  isRequireSelfie: boolean
  isRequireSms: boolean
  signingParams: IDraftEnvelopeRecipientSigningParams
}

export interface IEnvelopePostFlow {
  recipients: IEnveloperPostRecipient[]
}

export interface IEnveloperPostRecipient {
  type: TasksStatus
  email: string
  isHostMode: boolean
  guestEmail: string
  signer: IEnveloperSigner
  isAllowElectronicSignature: boolean
  isRequireSelfieToSignElectronically: boolean
  isRequireSmsToSignElectronically: boolean
  prepositionedMarks: IEnveloperPrepositionedMark[]
}

export interface IEnveloperSigner {
  fullName: string
  initials: Nullable<string>
  position: Nullable<string>
  phone: Nullable<number>
}

export interface IEnveloperCompletedSigner {
  oguid: string
  position: string
  email: string
  fullName: string
  initials: string
  phone: string
}

export interface IEnveloperCompletedEvidences {
  isVerifiedBySms: boolean
  phone: number
  isSelfieProvided: boolean
  selfieFileOguid: string
  geolocation: string
  userAgent: string
  recipientAccessAuthorizedBy: RecipientAccessAuthorizedBy
}

export interface IEnveloperPrepositionedMark {
  fileIndex: number
  key: string
  pageNumber: number
  topLeftX: number
  topLeftY: number
  height: number
  width: number
  type: string
  isReadOnly: boolean
  isRequired: boolean
  valueList: Nullable<string[]>
  value: Nullable<string>
  fontSize: number
  fontType: string
  font: string
  color: string
  scale: number
  tooltip: string
  label?: Nullable<string>
}

export interface IEnvelopeFiles {
  index: number
  filename: string
  originalProperties: IFileProperties
  signedProperties: Nullable<IFileProperties>
  pages: Nullable<string>
}

export interface IEnvelopeFields {
  [key: string]: boolean | number | string | null
}

export interface IEnvelopesListParams {
  direction: Nullable<EnvelopeDirection | undefined>
  boxOguid?: string
  page?: Nullable<string>
  perPage?: Nullable<string>
  // остальные параметры поиска
  otherSearchParams?: IParams
}

export interface IEnvelopeParams {
  oguid: string
  boxOguid: string
}

export interface IGetPreviewParams {
  fileUuid: string
  requestUuid?: string
}

export interface IPreviewDimensions {
  width: number,
  height: number
}

export interface IPreviewData {
  dimensions: IPreviewDimensions
  fileCode: string
  mimeType: string
  orientation: string
}

export interface IGetPreview extends IPreviewData {
  totalPages: number
}

export interface IEnvelopeFilePreview {
  data: IPreviewData[]
  fileName: string
  isAvailable: boolean
  total: number
}

export interface IDraftEnvelopeRecipientSigner {
  fullName: string
  initials: Nullable<string>
  position: Nullable<string>
  phone: Nullable<string>
}

export enum DraftEnvelopeRecipientType {
  ACQUAINTANCE = 'ACQUAINTANCE',
  APPROVAL = 'APPROVAL',
  SIGNING = 'SIGNING'
}

export enum DraftEnvelopeRecipientSignerType {
  PART = 'PART',
  WITNESS = 'WITNESS'
}

export interface IDraftEnvelopeRecipientSigningParams {
  isAllowElectronicSignature: boolean
  signerType: DraftEnvelopeRecipientSignerType
}

export interface IDraftEnvelopeRecipientData {
  boxEmail: string
  boxPhone: Nullable<string>
  boxId: string
  guestEmail: Nullable<string>
  isHostMode: boolean
  isRequireSelfie: boolean
  isRequireSms: boolean
  prepositionedMarks: Nullable<IDraftEnvelopePrepositionedMarkData[]>
  signer: IDraftEnvelopeRecipientSigner
  signingParams: IDraftEnvelopeRecipientSigningParams
  type: DraftEnvelopeRecipientType
}

export interface IDraftEnvelopeRecipientCustomParam {
  id: string
  label: string
  value: Nullable<string>
}

export interface IDraftEnvelopeRecipient extends IDraftEnvelopeRecipientData {
  color: string,
  customParams: IDraftEnvelopeRecipientCustomParam[]
  groupNum: number
}

export interface IDraftEnvelopePrepositionedMarkData {
  color: Nullable<string>
  fileIndex: number
  font: Nullable<DraftEnvelopePrepositionedMarkFont>
  fontSize: Nullable<number>
  fontType: Nullable<DraftEnvelopePrepositionedMarkFontType>
  height: Nullable<number>
  isReadOnly: Nullable<boolean>
  isRequired: Nullable<boolean>
  pageNumber: number
  scale: Nullable<number>
  tooltip: Nullable<string>
  topLeftX: number
  topLeftY: number
  type: DraftEnvelopePrepositionedMarkType,
  value: Nullable<string>
  valueList: Nullable<string[]>
  width: Nullable<number>
  /** лейбл к полю типа CUSTOM (обязательный для этого типа) */
  label?: Nullable<string>
  /** служебная связь с параметром получателя, в API не отправляется */
  customParamId?: Nullable<string>
}

export interface IDraftEnvelopePrepositionedMark extends IDraftEnvelopePrepositionedMarkData {
  recipient: string
}

export interface ISetDraftEnvelopePrepositionedMark {
  mark: Partial<IDraftEnvelopePrepositionedMark>
  uuid: string
}

export interface IDraftEnvelopeMessage {
  comment: Nullable<string>
  expirationDateTime: Nullable<Date>
  externalId: Nullable<string>
  subject: string
}

export interface IDraftEnvelopeField {
  key: string,
  value: Nullable<string>
}

export interface IDraftEnvelope {
  fields: Record<string, IDraftEnvelopeField>
  fieldsIdList: string[]
  files: string[]
  message: IDraftEnvelopeMessage
  preview: {
    [uuid: string]: IEnvelopeFilePreview
  }
  recipients: Record<string, IDraftEnvelopeRecipient>
  recipientsColors: string[]
  recipientsIdList: string[]
  prepositionedMarks: Record<string, IDraftEnvelopePrepositionedMark>
  prepositionedMarksIdList: string[]
}

export interface IGetPreviewThunkArg {
  fileOguid: string,
  page?: number
}

export interface IDraftEnvelopeFlowItem {
  recipients: Array<IDraftEnvelopeRecipientData>
}

export interface IDraftEnvelopeData {
  comment: Nullable<string>
  expirationDateTime: Nullable<string>
  externalId: Nullable<string>
  fields: Nullable<Record<string, string>>
  files: string[]
  flow: Array<IDraftEnvelopeFlowItem>
  senderBoxOguid: Nullable<string>
  subject: string
}

export interface IEnvelopesState {
  envelopes: {
    data: IEnvelope[],
    direction: Nullable<EnvelopeDirection | undefined>
  },
  checkedEnvelopes: Checkbox,
  currentEnvelope: Nullable<ICurrentEnvelope>
  draftEnvelope: IDraftEnvelope
  // oguid текущего ящика
  currentBoxOguid: string
}

/** Данные для получения хеша файла */
export interface IEnvelopeDoHashData {
  /** Сертификат в base64 */
  base64Certificate: string;
  /** OGUID бокса */
  boxOguid: string,
  /** OGUID МЧД */
  mchdOguid?: Nullable<string>
}

/** Данные для подписи файлов */
export interface IEnvelopeDoSignItem {
  /** Хэш подписываемого файла, нужен для его поиска */
  hash: string;
  /** Cформированная Cades-Bes подпись в hex и base64 форматах */
  signatureBase64: string;
}

/** Данные для doSign */
export interface IEnvelopeDoSignData {
  signatures: IEnvelopeDoSignItem[]
}
